import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Field, Panel, SectionTitle, TextArea } from '../../../../components';
import { firstErrorMessage, firstWarningMessage, issuesFor } from '../../issues-by-field';
import { previewUrl } from '../../preview-url';
import {
  platformChanged,
  resolveRouteText,
  setRouteText,
  titleTemplateFor,
  type ResolvedValue,
} from '../../seo-document';
import type { SiteSeoDocument, SiteSeoIssue, SiteSeoPage, SiteSeoRoute } from '../../seo-types';
import { DESCRIPTION_MAX, DESCRIPTION_MIN, TITLE_MAX, validateDocument } from '../../seo-validation';
import type { LiveMetaEntry } from '../../site-seo-state';
import { applyTitleTemplate, NAME_TOKEN, sampleName } from '../../title-template';
import { FieldTag, type FieldTagKind } from '../FieldTag';
import {
  Body,
  ErrorText,
  FieldBlock,
  Head,
  HintText,
  Note,
  Preview,
  PreviewDescription,
  PreviewEmpty,
  PreviewLabel,
  PreviewTitle,
  PreviewUrl,
  TagRow,
} from './SearchResultPanel.styles';

export type SearchResultPanelProps = {
  draft: SiteSeoDocument;
  published: SiteSeoDocument | null;
  /** The route's on-demand live meta (null = not asked for). */
  liveMeta: LiveMetaEntry | null;
  route: SiteSeoRoute;
  /** The site page behind the route, when there is one (`expanded` = the server fills `{{NAME}}`). */
  page?: SiteSeoPage;
  language: string;
  defaultLanguage: string;
  /** The site's address, e.g. `https://brand.com`. */
  siteUrl: string;
  /** A footer wildcard: meta is not set on a group of paths. */
  disabled: boolean;
  /** The server's issues (from the draft PUT); each field shows its first error as is. */
  issues: SiteSeoIssue[];
  onChange: (update: (draft: SiteSeoDocument) => SiteSeoDocument) => void;
  /** The Lumio generate control, beside the title. */
  generateSlot?: React.ReactNode;
};

type MetaField = 'title' | 'description';

/** The tag for a route field's shown value: none for the owner's own (or the site's) value. */
const tagOf = (resolved: ResolvedValue): FieldTagKind | null => {
  if (resolved.source === 'default-language') return 'using-default-language';
  if (resolved.source === 'platform') return 'platform-default';
  return null;
};

/** `value` with `token` in place of the `[start, end)` selection. */
const insertAt = (value: string, start: number, end: number, token: string): string =>
  value.slice(0, start) + token + value.slice(end);

/**
 * Search result for one route: the meta title and description with their counters, the Google preview (each value
 * falling back as the server serves it, see `resolveRouteText`), and the "Changed by the platform" note. A
 * dynamic page is edited as a template (`{{NAME}}` where the server fills the game name); a footer wildcard is inert.
 */
export const SearchResultPanel = ({
  draft,
  published,
  liveMeta,
  route,
  page,
  language,
  defaultLanguage,
  siteUrl,
  disabled,
  issues,
  onChange,
  generateSlot,
}: SearchResultPanelProps): React.ReactElement => {
  const { t } = useTranslation();
  const titleRef = React.useRef<HTMLDivElement>(null);
  const descriptionRef = React.useRef<HTMLDivElement>(null);
  const { path } = route;
  const dynamic = route.match === 'pattern';
  const nameSupported = dynamic && page?.expanded === true;
  const live = liveMeta?.status === 'ready' ? liveMeta.value : null;
  const template = draft.site.title_template?.[language] || draft.site.title_template?.[defaultLanguage] || undefined;
  const hints = validateDocument(draft, { defaultLanguage, siteHost: '', pages: page ? [page] : [] });
  const sample = (text: string): string => (dynamic ? sampleName(text, t('Game name')) : text);

  const own = (field: MetaField): string => route[field]?.[language] ?? '';
  const resolved = (field: MetaField): ResolvedValue =>
    resolveRouteText(draft, live, path, field, language, defaultLanguage);
  const set = (field: MetaField, value: string): void => {
    if (disabled) return;
    onChange((d) => setRouteText(d, path, field, language, value));
  };

  // The caret each field had when it lost the focus (to the insert button): where `{{NAME}}` goes. Never focused: the end.
  const caretRef = React.useRef<Partial<Record<MetaField, { start: number; end: number }>>>({});
  const rememberCaret = (field: MetaField, event: React.FocusEvent<HTMLDivElement>): void => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return;
    const start = target.selectionStart ?? target.value.length;
    caretRef.current[field] = { start, end: target.selectionEnd ?? start };
  };

  const insertName = (field: MetaField, block: React.RefObject<HTMLDivElement>): void => {
    if (disabled || !nameSupported) return;
    const value = own(field);
    const caret = caretRef.current[field];
    const start = Math.min(caret?.start ?? value.length, value.length);
    const end = Math.min(Math.max(caret?.end ?? start, start), value.length);
    set(field, insertAt(value, start, end, NAME_TOKEN));
    const after = start + NAME_TOKEN.length;
    caretRef.current[field] = { start: after, end: after };
    const control = block.current?.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
    requestAnimationFrame(() => {
      control?.focus();
      control?.setSelectionRange(after, after);
    });
  };

  const warn = (field: MetaField): boolean => {
    const value = own(field);
    if (field === 'title' && value.length > TITLE_MAX) return true;
    if (field === 'description' && value !== '' && (value.length < DESCRIPTION_MIN || value.length > DESCRIPTION_MAX))
      return true;
    return issuesFor(hints, path, field, language).length > 0;
  };

  const block = (field: MetaField, control: React.ReactNode, extra?: React.ReactNode): React.ReactElement => {
    const shown = resolved(field);
    const tag = own(field) === '' ? tagOf(shown) : null;
    const fieldIssues = issuesFor(issues, path, field, language);
    const error = firstErrorMessage(fieldIssues);
    const warning = error ? undefined : firstWarningMessage(fieldIssues);
    const tone = warning !== undefined || warn(field);
    const ref = field === 'title' ? titleRef : descriptionRef;
    return (
      <FieldBlock
        ref={ref}
        onBlur={(event) => rememberCaret(field, event)}
        data-field={field}
        data-tone={tone ? 'warning' : undefined}
        $warn={tone}
      >
        {control}
        {tag || (nameSupported && !disabled) ? (
          <TagRow>
            {tag ? <FieldTag kind={tag} language={defaultLanguage} /> : null}
            {nameSupported && !disabled ? (
              <Button label={t('Insert game name')} variant="text" icon="plus" onClick={() => insertName(field, ref)} />
            ) : null}
          </TagRow>
        ) : null}
        {extra}
        {error ? <ErrorText text={error} raw tag="span" /> : null}
        {warning ? <HintText text={warning} raw tag="span" /> : null}
      </FieldBlock>
    );
  };

  const placeholderOf = (field: MetaField): string | undefined =>
    own(field) === '' ? resolved(field).value || undefined : undefined;

  const shownTitle = ((): string => {
    const r = resolved('title');
    if (!r.value) return '';
    return sample(
      r.source === 'platform'
        ? r.value
        : applyTitleTemplate(titleTemplateFor(draft, r.source, language, defaultLanguage), r.value),
    );
  })();
  const shownDescription = sample(resolved('description').value);
  const titleValue = own('title');
  const descriptionValue = own('description');
  const changed =
    !disabled &&
    live !== null &&
    platformChanged(published, path, live, defaultLanguage, (p) =>
      applyTitleTemplate(published?.site.title_template?.[defaultLanguage], p),
    );

  return (
    <Panel>
      <Head>
        <SectionTitle title="Search result" />
        {!disabled ? generateSlot : null}
      </Head>
      <Body>
        {disabled ? <Note text="Meta is set on the site's pages, not on a group of paths." tag="paragraph" /> : null}
        {dynamic && !nameSupported && !disabled ? (
          <Note
            text="This page's address is dynamic: the same title and description apply to every page under it."
            tag="paragraph"
          />
        ) : null}
        {changed ? <Callout tone="warning" text="Changed by the platform since your last publish" /> : null}
        {block(
          'title',
          <Field
            label={t('Meta title')}
            value={titleValue}
            onChange={(value) => set('title', value)}
            placeholder={placeholderOf('title')}
            suffix={disabled ? undefined : `${titleValue.length}/${TITLE_MAX}`}
            disabled={disabled}
          />,
          template && titleValue && !disabled ? (
            <Note
              text="Shown as {{title}}"
              placeholders={{ title: sample(applyTitleTemplate(template, titleValue)) }}
              tag="span"
            />
          ) : null,
        )}
        {block(
          'description',
          <TextArea
            label={t('Meta description')}
            value={descriptionValue}
            onChange={(value) => set('description', value)}
            placeholder={placeholderOf('description')}
            hint={disabled ? undefined : `${descriptionValue.length}/${DESCRIPTION_MAX}`}
            rows={3}
            disabled={disabled}
          />,
        )}
        {!disabled ? (
          <Preview aria-label={t('Google preview')}>
            <PreviewLabel text="Google preview" tag="span" />
            <PreviewUrl text={previewUrl(siteUrl, path, language, defaultLanguage)} raw tag="span" />
            {shownTitle ? (
              <PreviewTitle text={shownTitle} raw tag="span" />
            ) : (
              <PreviewEmpty text="No title yet" tag="span" />
            )}
            {shownDescription ? (
              <PreviewDescription text={shownDescription} raw tag="span" />
            ) : (
              <PreviewEmpty text="No description yet" tag="span" />
            )}
          </Preview>
        ) : null}
      </Body>
    </Panel>
  );
};

export default SearchResultPanel;
